import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { Btn, StatusBadge } from "./common";
import { FieldInput } from "./RecordForm";

export default function ActionDialog({ module, record, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const t = module.transitions.find((x) => x.action === action);

  useEffect(() => {
    if (t) {
      setNote("");
      setError("");
      setInputs(Object.fromEntries(t.inputs.map((k) => [k, record.data[k] ?? ""])));
    }
  }, [action, record, t]);

  if (!t) return null;
  const fields = t.inputs.map((k) => module.fields.find((f) => f.key === k));

  const run = async () => {
    if (t.note && !note.trim()) { setError("A reason / note is required."); return; }
    setBusy(true); setError("");
    try {
      const res = await api.post(`/m/${module.key}/records/${record.id}/actions/${action}`, { note, inputs, version: record.version });
      toast.success(`${t.label}: done`);
      onDone(res.data);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="action-dialog">
        <DialogHeader>
          <DialogTitle>{t.label}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
              <span className="font-mono">{record.ref_no}</span>
              <StatusBadge status={record.status} statuses={module.statuses} />
              {t.to && <>→ <StatusBadge status={t.to} statuses={module.statuses} testid="action-target-status" /></>}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {fields.map((f) => <FieldInput key={f.key} f={{ ...f, required: true }} value={inputs[f.key]} onChange={(v) => setInputs((d) => ({ ...d, [f.key]: v }))} />)}
          <div>
            <label htmlFor="action-note" className="mb-1 block text-xs font-medium text-slate-700">Note / reason {t.note && <span className="text-red-600">*</span>}</label>
            <textarea id="action-note" data-testid="action-note-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
          </div>
          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="action-error">{error}</p>}
          <p className="text-xs text-slate-500">This action is permanent in the record history and audit trail.</p>
          <div className="flex justify-end gap-2">
            <Btn variant="outline" onClick={onClose} data-testid="action-cancel-btn">Cancel</Btn>
            <Btn variant={t.tone === "danger" ? "danger" : t.tone === "warn" ? "warn" : "brand"} disabled={busy} onClick={run} data-testid="action-confirm-btn">
              {busy ? "Processing…" : `Confirm: ${t.label}`}
            </Btn>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
